import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { LoaderCircle, Moon, Sun } from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { api } from "@/lib/api"
import type { PublicWidgetSettings } from "@/lib/types"

export function ChatbotTestPage() {
  const { id = "" } = useParams()
  const [chat, setChat] = useState<{ settings: PublicWidgetSettings; token: string; publicKey: string }>()
  const [error, setError] = useState<string>()
  const [dark, setDark] = useState(false)

  useEffect(() => {
    let cancelled = false
    setChat(undefined)
    setError(undefined)
    void (async () => {
      try {
        const detail = await api.getChatbot(id)
        const { token } = await api.previewSession(id)
        const settings = await api.publicConfig(detail.publicKey, token)
        if (!cancelled) setChat({ settings, token, publicKey: detail.publicKey })
      } catch (caught) {
        if (!cancelled) setError(caught instanceof Error ? caught.message : "Não foi possível abrir o teste.")
      }
    })()
    return () => { cancelled = true }
  }, [id])

  useEffect(() => {
    if (!chat) return
    const widgetScript = document.createElement("script") as HTMLScriptElement & { __bubbledeskCleanup?: () => void }
    widgetScript.src = `${window.location.origin}/widget.js`
    widgetScript.dataset.chatbot = chat.publicKey
    widgetScript.dataset.token = chat.token
    widgetScript.dataset.position = chat.settings.position
    widgetScript.dataset.launcherSize = String(chat.settings.launcherSize)
    widgetScript.dataset.widgetSize = chat.settings.widgetSize
    widgetScript.dataset.motionStyle = chat.settings.motionStyle
    document.body.appendChild(widgetScript)
    return () => {
      widgetScript.__bubbledeskCleanup?.()
      widgetScript.remove()
    }
  }, [chat])

  return <div className={`min-h-dvh ${dark ? "dark bg-slate-950 text-slate-50" : "bg-white text-slate-900"}`}>
    <header className="flex items-center justify-between gap-4 border-b border-current/10 px-5 py-4 sm:px-8">
      <Link to={`/chatbots/${id}/installation`} className="text-sm font-bold">BubbleDesk · Área de teste</Link>
      <Button variant="outline" size="icon" onClick={() => setDark((current) => !current)} aria-label={dark ? "Ativar tema claro" : "Ativar tema escuro"}>
        {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </Button>
    </header>
    <main className="mx-auto max-w-2xl px-6 py-20 text-center sm:py-24">
      <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">Teste interno</span>
      <h1 className="mt-5 text-3xl font-bold tracking-tight sm:text-4xl">Teste seu assistente por aqui.</h1>
      <p className="mx-auto mt-4 max-w-lg text-sm leading-6 opacity-70">Converse com o assistente usando as configurações publicadas. Esta área continua disponível quando o assistente está pausado nos sites.</p>
      {error ? <Alert variant="destructive" className="mt-8 text-left"><AlertTitle>Não foi possível iniciar o teste</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>
        : !chat && <LoaderCircle className="mx-auto mt-8 size-6 animate-spin" aria-label="Carregando assistente" />}
    </main>
  </div>
}
